// Global Instructions Rule Applied!
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// Pipeline Meetings (design §8.E) — response shapes of the
// /internal-agents/pipeline-meetings* read endpoints, strict-validated before
// any UI state. Keys follow the RPC output (table__column convention).
//
// NOTE: legacy ids can predate uuid formatting — z.string(), never .uuid()
// (bulk-add-prospects precedent). z.record always two-arg (Zod 4).

export const PipelineMeetingListItemSchema = z
  .object({
    pipeline_meetings__id: z.string(),
    pipeline_meetings__series_id: z.string(),
    pipeline_meeting_series__name: z.string(),
    pipeline_meetings__meeting_date: z.string(),
    pipeline_meetings__title: z.string().nullable(),
    pipeline_meetings__status: z.string(),
    prog__deal_count: z.number(),
    prog__minutes_ready: z.boolean()
  })
  .strict()

export const PipelineMeetingListSchema = z
  .object({
    data: z.array(PipelineMeetingListItemSchema),
    has_more: z.boolean(),
    count: z.number()
  })
  .strict()

export const MeetingDocSchema = z
  .object({
    pipeline_meeting_docs__id: z.string(),
    content: z.string(),
    // 'text' (pre-line plain text) | 'markdown' (AgentMarkdown). Optional so
    // a frontend deployed ahead of the 20261027120000 migration still reads.
    content_format: z.string().optional(),
    structured: z.record(z.string(), z.unknown()),
    generated_at: z.string().nullable(),
    // A person's edit (20261205120000): NULL = agent-written. Optional so a
    // frontend deployed ahead of the migration still reads.
    edited_by: z.string().nullable().optional(),
    prog__edited_by_name: z.string().nullable().optional(),
    prog__version_count: z.number().optional()
  })
  .strict()

// One minutes version (view__pipeline_meeting_minutes_history): agent
// generations, people's edits and restores, newest first.
export const MinutesVersionSchema = z
  .object({
    pipeline_meeting_docs__id: z.string(),
    content: z.string(),
    content_format: z.string().nullable(),
    generated_at: z.string().nullable(),
    is_current: z.boolean(),
    edited_by: z.string().nullable(),
    restored_from_id: z.string().nullable(),
    generated_by_run_id: z.string().nullable(),
    prog__revision: z.number(),
    prog__restored_from_revision: z.number().nullable(),
    prog__kind: z.enum(['agent', 'user', 'restore']),
    prog__author_name: z.string().nullable()
  })
  .strict()

export const MinutesVersionListSchema = z
  .object({
    data: z.array(MinutesVersionSchema),
    has_more: z.boolean(),
    count: z.number()
  })
  .strict()

export const MeetingNextStepSchema = z
  .object({
    description: z.string(),
    owner: z.string().nullable(),
    due_date: z.string().nullable(),
    source_excerpt: z.string().nullable()
  })
  .strict()

export const MeetingDealNoteSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullable(),
    accounts__name: z.string().nullable(),
    note_interaction_id: z.string().nullable(),
    // The deal follower's one-line takeaway (20261203120000). Optional so a
    // frontend image deployed before the migration still validates.
    key_point: z.string().nullable().optional(),
    summary: z.string().nullable(),
    next_steps: z.array(MeetingNextStepSchema).nullable(),
    thesis_update: z.string().nullable(),
    concerns: z.string().nullable(),
    generated_at: z.string().nullable()
  })
  .strict()

export const MeetingTranscriptSchema = z
  .object({
    meeting_transcripts__id: z.string(),
    transcript_text: z.string().nullable(),
    transcript_created_at: z.string().nullable()
  })
  .strict()

export const PipelineMeetingDetailSchema = z
  .object({
    pipeline_meetings__id: z.string(),
    pipeline_meetings__series_id: z.string(),
    pipeline_meeting_series__name: z.string(),
    pipeline_meetings__meeting_date: z.string(),
    pipeline_meetings__title: z.string().nullable(),
    pipeline_meetings__status: z.string(),
    minutes: MeetingDocSchema.nullable(),
    deal_notes: z.array(MeetingDealNoteSchema),
    transcript: MeetingTranscriptSchema.nullable()
  })
  .strict()

export const MeetingSeriesSummarySchema = z
  .object({
    pipeline_meeting_series__id: z.string(),
    pipeline_meeting_series__name: z.string(),
    meeting_count: z.number(),
    last_meeting_date: z.string().nullable()
  })
  .strict()

export const PipelineMeetingThemesSchema = z
  .object({
    series: MeetingSeriesSummarySchema.nullable(),
    themes: MeetingDocSchema.nullable()
  })
  .strict()

// One meeting's follow-ups (view__pipeline_meeting_follow_ups) — one row per
// agreed next step; owner_contact is the resolved staff contact (the task
// form's default assignee), tasks__* the linked task while it lives.
export const FollowUpOwnerContactSchema = z
  .object({
    contacts__id: z.string(),
    prog__display_name: z.string().nullable(),
    contacts__email: z.string().nullable()
  })
  .strict()

export const PipelineMeetingFollowUpSchema = z
  .object({
    pipeline_meeting_follow_ups__id: z.string(),
    pipeline_meeting_follow_ups__series_id: z.string(),
    pipeline_meeting_follow_ups__meeting_id: z.string(),
    pipeline_meeting_follow_ups__deal_id: z.string(),
    pipeline_meeting_follow_ups__deal_note_id: z.string().nullable(),
    pipeline_meeting_follow_ups__step_index: z.number(),
    pipeline_meeting_follow_ups__description: z.string(),
    pipeline_meeting_follow_ups__owner_name: z.string().nullable(),
    pipeline_meeting_follow_ups__due_date: z.string().nullable(),
    pipeline_meeting_follow_ups__source_excerpt: z.string().nullable(),
    pipeline_meeting_follow_ups__status: z.string(),
    pipeline_meeting_follow_ups__status_changed_by: z.string().nullable(),
    pipeline_meeting_follow_ups__status_changed_at: z.string().nullable(),
    pipeline_meeting_follow_ups__task_id: z.string().nullable(),
    // People's edits (20261205120000) — optional for deploy skew.
    pipeline_meeting_follow_ups__owner_contact_id: z.string().nullable().optional(),
    pipeline_meeting_follow_ups__edited_by: z.string().nullable().optional(),
    pipeline_meeting_follow_ups__edited_at: z.string().nullable().optional(),
    pipeline_meeting_follow_ups__original_description: z.string().nullable().optional(),
    prog__edited_by_name: z.string().nullable().optional(),
    prog__is_user_added: z.boolean().optional(),
    pipeline_meetings__meeting_date: z.string(),
    deals__name: z.string().nullable(),
    accounts__id: z.string().nullable(),
    accounts__name: z.string().nullable(),
    owner_contact: FollowUpOwnerContactSchema.nullable(),
    tasks__id: z.string().nullable(),
    tasks__subject: z.string().nullable(),
    tasks__completed: z.boolean().nullable(),
    tasks__due_by: z.string().nullable(),
    prog__effective_status: z.string(),
    prog__status_changed_by_name: z.string().nullable()
  })
  .strict()

export const PipelineMeetingFollowUpListSchema = z
  .object({
    data: z.array(PipelineMeetingFollowUpSchema),
    has_more: z.boolean(),
    count: z.number()
  })
  .strict()

export type PipelineMeetingListItem = z.infer<typeof PipelineMeetingListItemSchema>
export type PipelineMeetingList = z.infer<typeof PipelineMeetingListSchema>
export type MeetingDoc = z.infer<typeof MeetingDocSchema>
export type MeetingNextStep = z.infer<typeof MeetingNextStepSchema>
export type MeetingDealNote = z.infer<typeof MeetingDealNoteSchema>
export type PipelineMeetingDetail = z.infer<typeof PipelineMeetingDetailSchema>
export type PipelineMeetingThemes = z.infer<typeof PipelineMeetingThemesSchema>
export type PipelineMeetingFollowUp = z.infer<typeof PipelineMeetingFollowUpSchema>
export type PipelineMeetingFollowUpList = z.infer<typeof PipelineMeetingFollowUpListSchema>
export type MinutesVersion = z.infer<typeof MinutesVersionSchema>
export type MinutesVersionList = z.infer<typeof MinutesVersionListSchema>

export const validatePipelineMeetingList = (data: unknown): PipelineMeetingList =>
  validateWithZodSchema(PipelineMeetingListSchema, data, 'pipeline meetings list')

export const validatePipelineMeetingDetail = (data: unknown): PipelineMeetingDetail =>
  validateWithZodSchema(PipelineMeetingDetailSchema, data, 'pipeline meeting detail')

export const validatePipelineMeetingThemes = (data: unknown): PipelineMeetingThemes =>
  validateWithZodSchema(PipelineMeetingThemesSchema, data, 'pipeline meeting themes')

export const validatePipelineMeetingFollowUpList = (data: unknown): PipelineMeetingFollowUpList =>
  validateWithZodSchema(PipelineMeetingFollowUpListSchema, data, 'pipeline meeting follow-ups')

export const validateMinutesVersionList = (data: unknown): MinutesVersionList =>
  validateWithZodSchema(MinutesVersionListSchema, data, 'pipeline meeting minutes history')
